import {
  barristerCfaApplies,
  ConditionalFeeAgreement,
  ConditionType,
  getSuccessFeePercentage,
  LOWEST_MINIMUM_COMPENSATION,
  PartyAgreements,
  SuccessFee,
  SuccessStep,
} from "../../shared/Agreements.ts";

/**
 * Is there a barrister CFA that is switched on and yet without effect? The
 * law firm's CFA covering barrister fees takes precedence over a CFA the
 * party itself has with the barrister.
 */
export function barristerCfaIsMoot(agreements: PartyAgreements): boolean {
  return (
    agreements.barristerCfa?.active === true &&
    !barristerCfaApplies(agreements)
  );
}

/**
 * Who has to engage the barrister under a CFA while the law firm has a CFA of
 * its own: the law firm if that covers barrister fees, the party if it does
 * not. Undefined without a law-firm CFA — either may engage the barrister
 * then.
 */
export function requiredEngagement({
  lawFirmCfa,
}: PartyAgreements): "lawFirm" | "party" | undefined {
  if (lawFirmCfa?.active !== true) return undefined;
  return lawFirmCfa.includedDisbursements.includes("barristerFee")
    ? "lawFirm"
    : "party";
}

/**
 * Whether the law firm's CFA has to cover barrister fees while the barrister
 * is under a CFA as well: it has to if the law firm engaged the barrister,
 * and cannot if the party did. Undefined without a barrister CFA — it may do
 * either then.
 */
export function requiredBarristerFeeCover({
  barristerCfa,
}: PartyAgreements): boolean | undefined {
  if (barristerCfa?.active !== true) return undefined;
  return barristerCfa.engagedBy === "lawFirm";
}

/** A success step on its own, without its link to the next one. */
export type Step = Omit<SuccessStep, "next">;

/**
 * The trigger amount an entered amount is stored as. Trigger amounts are
 * compared against what the outcome is worth to the party, so an amount to
 * pay is stored below zero: a maximum of 500 to pay is -500. A minimum to
 * recover or to be awarded is stored as entered, though never below the
 * lowest one there is.
 */
export function storedTriggerAmount(
  enteredAmount: number,
  conditionType: ConditionType
): number {
  const amount = Math.abs(enteredAmount);
  if (conditionType === ConditionType.maximumCompensationToPay)
    return amount > 0 ? -amount : 0;
  return Math.max(LOWEST_MINIMUM_COMPENSATION, amount);
}

/**
 * The steps of a success fee in the order they are chained, each trigger
 * amount as its condition type stores it.
 */
export function stepsOf({ conditionType, step: first }: SuccessFee): Step[] {
  const steps: Step[] = [];
  for (let step: SuccessStep | undefined = first; step; step = step.next)
    steps.push({
      triggerAmount: storedTriggerAmount(step.triggerAmount, conditionType),
      successFeePercentage: step.successFeePercentage,
    });
  return steps;
}

/** Chains steps into the form a success fee stores them in. */
export function chainOf(steps: Step[]): SuccessStep {
  const [first, ...rest] = steps;
  if (first === undefined)
    throw new Error("A success fee needs at least one step");
  // only the link made here: a step handed in may still carry an old one
  const { triggerAmount, successFeePercentage } = first;
  return rest.length > 0
    ? { triggerAmount, successFeePercentage, next: chainOf(rest) }
    : { triggerAmount, successFeePercentage };
}

export type StepTerms = Step & {
  /**
   * What is charged once the trigger amount of this step is reached: the
   * highest percentage among all steps reached with it.
   */
  appliedPercentage: number;
  /** The success fee: the applied percentage of the base costs at risk. */
  uplift: number;
  /** Base costs and success fee together. */
  total: number;
  /**
   * The trigger amount of the next step up, from where this step no longer
   * decides the fee. Undefined for the step with the highest trigger amount.
   */
  nextTriggerAmount?: number;
};

export type CfaTerms = {
  /** Fee due when no step is reached. */
  noWinFee: number;
  /**
   * Base costs not paid when no step is reached. The success fee is a
   * percentage of these, so a higher no-win fee means a lower success fee.
   */
  atRisk: number;
  steps: StepTerms[];
  /** Some step does not start above the one before it. */
  unordered: boolean;
  /** Some step changes no fee: a step reached with it charges as much or more. */
  ineffective: boolean;
};

/** What an agreement charges on base costs of `baseCost`, per step and without one. */
export function cfaTerms(
  { successFee, lowFee }: Pick<ConditionalFeeAgreement, "successFee" | "lowFee">,
  baseCost: number
): CfaTerms {
  const steps = stepsOf(successFee);
  const chain = chainOf(steps);
  const noWinFee = baseCost * (lowFee?.discountPercentage ?? 0);
  const atRisk = baseCost - noWinFee;

  return {
    noWinFee,
    atRisk,
    steps: steps.map((step) => {
      // a step is reached at its own trigger amount, so there always is one
      const appliedPercentage =
        getSuccessFeePercentage(step.triggerAmount, {
          conditionType: successFee.conditionType,
          step: chain,
        }) ?? step.successFeePercentage;
      const uplift = atRisk * appliedPercentage;
      const higher = steps
        .map(({ triggerAmount }) => triggerAmount)
        .filter((triggerAmount) => triggerAmount > step.triggerAmount);
      return {
        ...step,
        appliedPercentage,
        uplift,
        total: baseCost + uplift,
        nextTriggerAmount: higher.length > 0 ? Math.min(...higher) : undefined,
      };
    }),
    unordered: steps.some(
      ({ triggerAmount }, index) =>
        index > 0 && triggerAmount <= steps[index - 1].triggerAmount
    ),
    ineffective: steps.some((step, index) =>
      steps.some(
        (other, otherIndex) =>
          otherIndex !== index &&
          other.triggerAmount <= step.triggerAmount &&
          other.successFeePercentage >= step.successFeePercentage
      )
    ),
  };
}
